import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aurora brand mark — a compact glyph paired with the wordmark.
 *
 * The glyph is a CSS-only "stacked sheet" symbol that nods at the product
 * (filling documents) without resorting to a generic clipboard icon. Pure
 * gradient + clip-path, no SVG payload.
 *
 * Size variants line the wordmark up with the surrounding type.
 */
interface BrandMarkProps extends React.ComponentProps<'div'> {
  size?: 'sm' | 'md' | 'lg'
  wordmark?: boolean
  product?: string
}

const sizeMap: Record<NonNullable<BrandMarkProps['size']>, {
  glyph: string
  text: string
  gap: string
}> = {
  sm: { glyph: 'size-5', text: 'text-sm', gap: 'gap-2' },
  md: { glyph: 'size-7', text: 'text-base', gap: 'gap-2.5' },
  lg: { glyph: 'size-9', text: 'text-lg', gap: 'gap-3' },
}

function BrandMark({
  size = 'md',
  wordmark = true,
  product = 'DocFiller',
  className,
  ...props
}: BrandMarkProps) {
  const s = sizeMap[size]
  return (
    <div
      data-slot="brand-mark"
      className={cn('inline-flex items-center', s.gap, className)}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center rounded-md',
          'bg-gradient-to-br from-primary to-primary/70',
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_-4px_color-mix(in_oklch,var(--color-primary)_60%,transparent)]',
          s.glyph,
        )}
      >
        {/* Stacked-sheet glyph — two offset rounded rectangles in white,
            translucent. Reads as documents without being literal. */}
        <span className="absolute inset-[18%] rounded-[3px] bg-white/35" />
        <span className="absolute inset-[28%] translate-x-[8%] translate-y-[8%] rounded-[3px] bg-white/90" />
      </span>
      {wordmark ? (
        <span
          className={cn(
            'font-semibold tracking-tight text-foreground',
            s.text,
          )}
        >
          {product}
        </span>
      ) : null}
    </div>
  )
}

export { BrandMark }
